/**
 * Lobby : code en hero, invitation copiable, grille d'avatars, reglages
 * editables par l'hote seul.
 */
import { useCallback, useState, type ReactElement } from 'react';
import { MIN_PLAYERS, minPlayersFor, type RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { leaveRoom } from '../net/leaveRoom.js';
import { useIsHost, useRoomStore } from '../store/roomStore.js';
import { Avatar, AvatarSlot } from '../ui/Avatar.js';
import { BotTag } from '../ui/BotTag.js';
import { StreamTag } from '../ui/StreamTag.js';
import { Screen } from '../ui/Screen.js';
import { ChevronLeftIcon } from '../ui/icons.js';
import { navigate, roomPath } from '../router.js';
import { showPreroll } from '../ads/adapter.js';
import { GameSettings } from './GameSettings.js';

export function LobbyScreen({ room }: { room: RoomSnapshot }): ReactElement {
  const t = useT();
  const isHost = useIsHost();
  const playerId = useRoomStore((state) => state.playerId);
  const [copied, setCopied] = useState(false);

  const host = room.players.find((player) => player.id === room.hostId);
  // Sur un salon public, personne ne regle rien : ni l'hote, ni les autres.
  const canEditSettings = isHost && !room.settingsLocked;
  // L'effectif minimal DEPEND DU MODE : trois joueurs des qu'on vote. Le
  // bouton se grisait a deux alors que le serveur en exigeait trois, et le
  // refus arrivait apres le clic, avec un message qui annoncait le mauvais
  // chiffre.
  const needed = minPlayersFor(room.settings.mode);
  const enoughPlayers = room.players.length >= needed;

  const copyInvite = useCallback(() => {
    const url = `${window.location.origin}${roomPath(room.code)}`;
    void navigator.clipboard
      ?.writeText(url)
      .catch(() => undefined)
      .finally(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      });
  }, [room.code]);

  const leave = useCallback(() => {
    leaveRoom();
    navigate('/');
  }, []);

  const emptySlots = Math.max(0, needed - room.players.length);

  return (
    <Screen name="lobby" scroll>
      <main className="lobby">
        {/* La sortie est EN HAUT, avant tout le reste.
            Elle vivait au pied d'une page de reglages qui defile : sur
            telephone il fallait passer le mode, les categories, les manches,
            les durees et le plafond d'effectif pour la trouver. Ne restant
            rien de visible pour sortir, l'ecran se lisait comme un cul-de-sac.
            Elle est ici seule : deux sorties qui font la meme chose sur un
            meme ecran, c'est une de trop. */}
        <div className="lobby__nav">
          <button type="button" className="button-back" onClick={leave}>
            <ChevronLeftIcon />
            {t('lobby.leave')}
          </button>
        </div>

        <header className="lobby__header">
          <p className="lobby__label">{t('lobby.title')}</p>
          <p className="lobby__code mono-tabular">{room.code}</p>
          <button type="button" className="button-ghost" onClick={copyInvite}>
            {copied ? t('lobby.copied') : t('lobby.copyInvite')}
          </button>
        </header>

        <section className="lobby__players">
          <p className="section-label">
            {/* Le plafond du SALON, pas celui du jeu : sinon un salon limite a
                3 affichait « 3/10 » tout en refusant les arrivees. */}
            {t('lobby.players', { count: room.players.length, max: room.settings.maxPlayers })}
          </p>
          <div className="avatar-grid">
            {room.players.map((player) => (
              <div className="avatar-grid__item" key={player.id}>
                <Avatar
                  playerId={player.id} avatar={player.avatar}
                  colorIndex={player.colorIndex}
                  size={56}
                  dimmed={!player.connected}
                />
                <span className="avatar-grid__name">
                  {player.nickname}
                  {player.bot && <BotTag />}
                  {player.streamer && <StreamTag />}
                  {player.id === playerId && <span className="avatar-grid__tag"> · {t('common.you')}</span>}
                  {player.id === room.hostId && <span className="avatar-grid__tag"> · {t('common.host')}</span>}
                </span>
              </div>
            ))}
            {Array.from({ length: emptySlots }, (_, index) => (
              <div className="avatar-grid__item" key={`slot-${index}`}>
                <AvatarSlot size={56} />
              </div>
            ))}
          </div>
        </section>

        <section className="lobby__settings">
          <GameSettings room={room} canEdit={canEditSettings} />
          {room.settingsLocked && <p className="hint">{t('lobby.publicSettings')}</p>}
        </section>

        <footer className="lobby__footer">
          {isHost ? (
            <>
              {/* AU-DESSUS du bouton, et en rouge. Sous le bouton, on lisait la
                  raison APRES avoir essaye de cliquer ; en gris, elle passait
                  pour une note de bas de page alors qu'elle explique un refus.
                  Et quand le mode exige plus que le minimum ordinaire, le texte
                  le DIT : « pour ce mode » est la seule chose qui empeche de
                  croire a un bug quand on a deja deux joueurs a table. */}
              {!enoughPlayers && (
                <p className="lobby__blocker">
                  {needed > MIN_PLAYERS
                    ? t('lobby.needPlayersMode', { n: needed })
                    : t('lobby.needPlayers', { n: needed })}
                </p>
              )}
              <button
                type="button"
                className="button-primary button-block"
                disabled={!enoughPlayers}
                onClick={() => void showPreroll().finally(() => connection.send({ type: 'start' }))}
              >
                {t('lobby.start')}
              </button>
            </>
          ) : (
            <p className="lobby__waiting">
              {t('lobby.waitingForHost', { host: host?.nickname ?? '…' })}
            </p>
          )}
        </footer>
      </main>
    </Screen>
  );
}
